import {
  default as React,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useDebounce from 'react-use/lib/useDebounce'
import { distanceInWordsToNow } from 'date-fns'
import useTitle from 'react-use/lib/useTitle'

import { FilterUsersReq, requestUsers } from './usersActions'
import Search from '../components/Search'
import { IUser } from '../types/user'
import name from '../common/name'
import Avatar from '../components/Avatar'
import useThunkDispatch from '../common/useThunkDispatch'
import Splash from '../components/Splash'
import Pager from '../components/Pager'
import {
  usersPaginationStateSelector,
  usersSelector,
  usersTotalSelector,
} from './usersSelectors'

const PER_PAGE = 50

const InternalUsers: FunctionComponent = () => {
  useTitle('Internal Users - Forshaw')

  const dispatch = useThunkDispatch()

  const users: IUser[] = useSelector(usersSelector)
  const paginationState = useSelector(usersPaginationStateSelector)
  const total = useSelector(usersTotalSelector)

  const [page, setPage] = useState<number>(0)
  const [term, setTerm] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const loadUsers = async (req: FilterUsersReq, newPage: number) => {
    setLoading(true)
    await dispatch(requestUsers(req))
    setPage(newPage)
    setLoading(false)
  }

  const handlePrevious = () => {
    loadUsers(
      {
        last: PER_PAGE,
        before: paginationState.startCursor,
        text: term || undefined,
      },
      page - 1,
    )
  }

  const handleNext = () => {
    loadUsers(
      {
        first: PER_PAGE,
        after: paginationState.endCursor,
        text: term || undefined,
      },
      page + 1,
    )
  }

  useDebounce(
    async () => {
      if (term == null) return
      await loadUsers({ first: PER_PAGE, text: term || undefined }, 0)
    },
    300,
    [term],
  )

  useEffect(() => {
    loadUsers({ first: PER_PAGE }, 0)
  }, [])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [page, term])

  return (
    <div className="flexible-container table-page-layout">
      <div className="filter">
        <div className="filter_col -large">
          <Search
            placeholder="Search Internal User"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="table">
        <div className="table_head-tr">
          <div className="table_td -avatar" />
          <div className="table_td -g33 table_title">Users</div>
          <div className="table_td -g33 table_title">Email</div>
          <div className="table_td -g33 table_title">Last log in</div>
        </div>
      </div>
      <div className="table_loading-wrapper">
        <div
          className="wonder-scroll"
          ref={scrollContainerRef}
          style={{ height: '100%' }}
        >
          <div className="table">
            {users.length === 0 && !loading && (
              <div className="table_head-tr">
                <div className="table_td -avatar" />
                <div className="table_head-tr table_td table_title">
                  {!!term && 'No users found, please adjust search criteria'}
                  {!term && 'No users found'}
                </div>
              </div>
            )}

            {users.map((user) => (
              <div className="table_tr" key={user.id}>
                <div className="table_td -avatar">
                  <Avatar user={user} />
                </div>
                <div className="table_td -g33">{name(user)}</div>
                <div className="table_td -g33">{user.email}</div>
                <div className="table_td -g33">
                  {user.lastSignInAt &&
                    `${distanceInWordsToNow(user.lastSignInAt)} ago`}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Splash isLoading={loading} bgColor="#F2F6FA" />
      </div>

      <Pager
        page={page}
        total={total}
        perPage={PER_PAGE}
        previousEnabled={
          paginationState && paginationState.hasPreviousPage && !loading
        }
        nextEnabled={paginationState && paginationState.hasNextPage && !loading}
        onNext={handleNext}
        onPrevious={handlePrevious}
        className="-right"
      />
    </div>
  )
}

export default InternalUsers
